import noop from 'utils/noop';
import { useLocalStorage } from 'react-use';
import { useMemo } from 'react';
import { DevPanelFilter, DevPanelFilterKeys, DevToolStateFlags } from '../DevPanel.types';
import { getLocalStorageKey, getDevToolStates } from '../DevPanel.utils';
import { KNOWN_STATES } from '../DevPanel.constants';

export const DEFAULT_DEV_TOOL_UI_STATES: DevToolStateFlags<string> = {
  isDevToolDataStateEnabled: false,
  isDevToolErrorStateEnabled: false,
  isDevToolLoadingStateEnabled: false,
  isDevToolEmptyStateEnabled: false,
  isDevToolZeroStateEnabled: false,
  isDevToolNoneStateEnabled: false,
};

export const DEFAULT_USE_DEV_PANEL_STATE: UseDevPanelState = Object.freeze({
  isDevPanelOpen: false,
  setIsDevPanelOpen: noop,
  devPanelSendsState: KNOWN_STATES.NONE,
  setDevPanelSendsState: noop,
  devPanelGoalsState: KNOWN_STATES.NONE,
  setDevPanelGoalsState: noop,
  devPanelAlertsState: KNOWN_STATES.NONE,
  setDevPanelAlertsState: noop,
  devPanelUserActivityState: KNOWN_STATES.NONE,
  setDevPanelUserActivityState: noop,
  devPanelActiveFilters: [],
  devPanelSelectedFiltersCount: 0,
  clearDevPanelFilters: noop,
  devToolAlertsStates: DEFAULT_DEV_TOOL_UI_STATES,
  devToolUserActivityStates: DEFAULT_DEV_TOOL_UI_STATES,
  devToolGoalsStates: DEFAULT_DEV_TOOL_UI_STATES,
  devToolSendsStates: DEFAULT_DEV_TOOL_UI_STATES,
});

export type UseDevPanelState = ReturnType<typeof useDevPanel>;

export const useDevPanel = () => {
  const [isDevPanelOpen, setIsDevPanelOpen] = useLocalStorage(
    getLocalStorageKey('isDevPanelOpen'),
    false,
  );

  const [devPanelSendsState = KNOWN_STATES.NONE, setDevPanelSendsState] = useLocalStorage(
    getLocalStorageKey(DevPanelFilterKeys.SENDS),
    KNOWN_STATES.NONE,
  );
  const [devPanelGoalsState = KNOWN_STATES.NONE, setDevPanelGoalsState] = useLocalStorage(
    getLocalStorageKey(DevPanelFilterKeys.GOALS),
    KNOWN_STATES.NONE,
  );
  const [devPanelAlertsState = KNOWN_STATES.NONE, setDevPanelAlertsState] = useLocalStorage(
    getLocalStorageKey(DevPanelFilterKeys.ALERTS),
    KNOWN_STATES.NONE,
  );
  const [devPanelUserActivityState = KNOWN_STATES.NONE, setDevPanelUserActivityState] =
    useLocalStorage(getLocalStorageKey(DevPanelFilterKeys.USER_ACTIVITY), KNOWN_STATES.NONE);

  const devPanelActiveFilters: DevPanelFilter[] = useMemo(() => {
    const filters = [
      { id: DevPanelFilterKeys.SENDS, value: devPanelSendsState, label: 'Sends' },
      { id: DevPanelFilterKeys.GOALS, value: devPanelGoalsState, label: 'Goals' },
      { id: DevPanelFilterKeys.ALERTS, value: devPanelAlertsState, label: 'Alerts' },
      {
        id: DevPanelFilterKeys.USER_ACTIVITY,
        value: devPanelUserActivityState,
        label: 'User Activity',
      },
    ];

    return filters.filter(({ value }) => Boolean(value) && value !== KNOWN_STATES.NONE);
  }, [devPanelSendsState, devPanelGoalsState, devPanelAlertsState, devPanelUserActivityState]);

  const devPanelSelectedFiltersCount = devPanelActiveFilters.length;

  const clearDevPanelFilters = () => {
    setDevPanelSendsState(KNOWN_STATES.NONE);
    setDevPanelAlertsState(KNOWN_STATES.NONE);
    setDevPanelGoalsState(KNOWN_STATES.NONE);
    setDevPanelUserActivityState(KNOWN_STATES.NONE);
  };

  const devToolGoalsStates = getDevToolStates(devPanelGoalsState);
  const devToolSendsStates = getDevToolStates(devPanelSendsState);
  const devToolAlertsStates = getDevToolStates(devPanelAlertsState);
  const devToolUserActivityStates = getDevToolStates(devPanelUserActivityState);

  return {
    isDevPanelOpen,
    setIsDevPanelOpen,
    devPanelSendsState,
    setDevPanelSendsState,
    devPanelGoalsState,
    setDevPanelGoalsState,
    devPanelAlertsState,
    setDevPanelAlertsState,
    devPanelUserActivityState,
    setDevPanelUserActivityState,
    devPanelActiveFilters,
    devPanelSelectedFiltersCount,
    clearDevPanelFilters,
    devToolAlertsStates,
    devToolGoalsStates,
    devToolSendsStates,
    devToolUserActivityStates,
  };
};
